// src/validators/contactValidators.js

const stripDiacritics = (value) => {
  if (!value) return "";
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

const normalizeEmail = (value) => {
  const v = stripDiacritics(String(value || "").trim()).toLowerCase();
  return v;
};

const isValidEmail = (value) => {
  const v = normalizeEmail(value);
  if (!v) return false;
  // Simple + safe email regex for UI validation
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  return re.test(v);
};

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const isValidBRPhoneLocal = (value) => {
  // Brazil mobile/landline typically 10-11 digits (DDD + number)
  const d = digitsOnly(value);
  return d.length === 10 || d.length === 11;
};

const formatPhoneE164BR = (value) => {
  const raw = String(value || "").trim();
  const d = digitsOnly(raw);
  if (!d) return "";

  // If the user explicitly typed a "+" country code (e.g. "+48577348405"),
  // honor it as-is instead of assuming Brazil - otherwise foreign numbers
  // silently get a "+55" prefix slapped in front of them.
  if (raw.startsWith("+")) {
    if (d.length < 8 || d.length > 15) return "";
    return `+${d}`;
  }

  // No explicit country code -> assume a Brazilian local number (DDD +
  // number), optionally already containing the "55" country code.
  // If user pasted "+55..." -> we strip to digits and re-add
  // If user pasted "5533..." -> same
  const withoutCountry =
    d.startsWith("55") && (d.length === 12 || d.length === 13)
      ? d.slice(2)
      : d;

  if (!(withoutCountry.length === 10 || withoutCountry.length === 11)) return "";
  return `+55${withoutCountry}`;
};

const isValidCPF = (value) => {
  const cpf = digitsOnly(value);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(cpf[i]) * (10 - i);
  let d1 = (sum * 10) % 11;
  if (d1 === 10) d1 = 0;
  if (d1 !== Number(cpf[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number(cpf[i]) * (11 - i);
  let d2 = (sum * 10) % 11;
  if (d2 === 10) d2 = 0;
  return d2 === Number(cpf[10]);
};

const isValidCNPJ = (value) => {
  const cnpj = digitsOnly(value);
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false;

  const calc = (base) => {
    let sum = 0;
    let pos = base.length - 7;
    for (let i = base.length; i >= 1; i--) {
      sum += Number(base[base.length - i]) * pos--;
      if (pos < 2) pos = 9;
    }
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const base12 = cnpj.slice(0, 12);
  const d1 = calc(base12);
  const d2 = calc(base12 + String(d1));
  return cnpj === base12 + String(d1) + String(d2);
};

const isValidRandomPixKey = (value) => {
  // Pix random key usually UUID format
  const v = String(value || "").trim();
  const re =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return re.test(v);
};

const normalizePixKeyByType = (pixType, value) => {
  const raw = String(value || "").trim();

  switch (pixType) {
    case "CPF": {
      const d = digitsOnly(raw);
      return d;
    }
    case "CNPJ": {
      const d = digitsOnly(raw);
      return d;
    }
    case "Telefone": {
      // For PIX phone: must be local digits only (no +55) per your rule
      return digitsOnly(raw);
    }
    case "E-mail": {
      return normalizeEmail(raw);
    }
    case "Chave Aleatória": {
      return raw.toLowerCase();
    }
    default:
      return raw;
  }
};

const validatePixKeyByType = (pixType, value) => {
  const raw = String(value || "").trim();
  if (!pixType) return { ok: true, value: "" }; // no pix type chosen => treat as absent
  if (!raw) return { ok: false, error: "Informe a chave PIX." };

  switch (pixType) {
    case "CPF": {
      const d = digitsOnly(raw);
      if (d.length !== 11 || !isValidCPF(d)) return { ok: false, error: "CPF inválido." };
      return { ok: true, value: d };
    }
    case "CNPJ": {
      const d = digitsOnly(raw);
      if (d.length !== 14 || !isValidCNPJ(d)) return { ok: false, error: "CNPJ inválido." };
      return { ok: true, value: d };
    }
    case "Telefone": {
      const d = digitsOnly(raw);
      // local digits only (DDD + number)
      if (!(d.length === 10 || d.length === 11)) {
        return { ok: false, error: "Telefone PIX inválido (use DDD + número, sem +55)." };
      }
      return { ok: true, value: d };
    }
    case "E-mail": {
      if (!isValidEmail(raw)) return { ok: false, error: "E-mail inválido." };
      return { ok: true, value: normalizeEmail(raw) };
    }
    case "Chave Aleatória": {
      if (!isValidRandomPixKey(raw)) return { ok: false, error: "Chave aleatória inválida (UUID)." };
      return { ok: true, value: raw.toLowerCase() };
    }
    default:
      return { ok: false, error: "Tipo de PIX inválido." };
  }
};

export const ContactValidators = {
  normalizeEmail,
  isValidEmail,
  formatPhoneE164BR,
  isValidBRPhoneLocal,
  validatePixKeyByType,
  normalizePixKeyByType,
  digitsOnly,
  isValidCPF,
  isValidCNPJ,
};
